import { useTranslation } from 'react-i18next';

import { View } from '@react-pdf/renderer';

import {
  IKeyFeaturesItem,
  KEY_FEATURES_NAMES,
} from 'components/BusinessToolForm/Sections/ProductsAndServices/ProductAndServicesDescription/utils/form';

import { Certificate } from '../Certificate/Certificate';
import { ITextItem, PdfContentText } from '../PdfContentText';
import { ProductCharacteristics } from '../ProductCharacteristics/ProductCharacteristics';
import { SmallHeading } from '../SmallHeading';
import { SubChapterHeading } from '../SubChapterHeading';

export interface ICharacteristic {
  companyImageSrc: string;
  companyName: string;
  features: IKeyFeaturesItem[];
}

export interface ICertifications {
  file: string;
  value: string;
  description: string | IKeyFeaturesItem[];
}

interface IProductsServicesDescriptionProps {
  id: string;
  heading: string;
  magazineItems: Array<ITextItem[]>;
  characteristics: ICharacteristic[];
  certifications: ICertifications[];
  patentsOrTrademarks: ICertifications[];
  isExamplePage: boolean;
}

export const ProductsServicesDescription = ({
  id,
  heading,
  magazineItems,
  characteristics,
  certifications,
  patentsOrTrademarks,
  isExamplePage,
}: IProductsServicesDescriptionProps) => {
  const [firstCharacteristic, ...otherCharacteristics] = characteristics;
  const { t } = useTranslation();

  return (
    <>
      <SubChapterHeading id={id} heading={heading} />
      <PdfContentText
        items={magazineItems[0]}
        withoutBottomMargin={characteristics.length === 0}
        withoutGapBetweenItems={characteristics.length === 0}
      />
      {characteristics.length > 0 && (
        <>
          <View wrap={false}>
            <SmallHeading
              heading={
                isExamplePage
                  ? 'Product/Service Characteristics'
                  : t('pdfPages.mappings.productsAndServices.productsServicesCharacteristics')
              }
              minPresenceAhead={500}
            />
            <ProductCharacteristics
              items={firstCharacteristic.features.map((characteristic, index) => ({
                step: `0${index + 1}`,
                name: characteristic[KEY_FEATURES_NAMES.FEATURE],
                description: characteristic[KEY_FEATURES_NAMES.DESCRIPTION],
              }))}
              companyImageSrc={firstCharacteristic.companyImageSrc}
              companyName={firstCharacteristic.companyName}
            />
          </View>
          {otherCharacteristics.map(({ companyImageSrc, companyName, features }, index) => (
            <ProductCharacteristics
              key={companyName + index}
              items={features.map((characteristic, index) => ({
                step: `0${index + 1}`,
                name: characteristic[KEY_FEATURES_NAMES.FEATURE],
                description: characteristic[KEY_FEATURES_NAMES.DESCRIPTION],
              }))}
              companyImageSrc={companyImageSrc}
              companyName={companyName}
            />
          ))}
        </>
      )}
      <PdfContentText items={magazineItems[1]} withoutGapBetweenItems />
      {certifications.length > 0 && (
        <>
          {certifications.map((certificate, index) => (
            <Certificate
              key={certificate.value}
              img={certificate.file}
              heading={certificate.value}
              text={certificate.description as string}
              title={
                index === 0
                  ? isExamplePage
                    ? 'Quality Certifications'
                    : t('pdfPages.mappings.productsAndServices.qualityCertifications')
                  : ''
              }
            />
          ))}
        </>
      )}
      {patentsOrTrademarks.length > 0 && (
        <>
          {patentsOrTrademarks.map((certificate, index) => (
            <Certificate
              key={certificate.value}
              img={certificate.file}
              heading={certificate.value}
              text={certificate.description as string}
              title={
                index === 0
                  ? isExamplePage
                    ? 'Intellectual Property'
                    : t('pdfPages.mappings.productsAndServices.intellectualProperty')
                  : ''
              }
            />
          ))}
        </>
      )}
    </>
  );
};
